/**
 * AccountsPage Component
 *
 * Main page for managing trading accounts.
 * Desktop: Stacked card layout with Account selector card + Account Config tabs card.
 * Mobile: Nested page navigation via Routes.
 *
 * State Management:
 * - Data state (accounts, selection, dirty tracking) → AccountsContext
 * - UI state (tabs, modals, etc.) → Local component state
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Routes, Route } from 'react-router-dom';
import { Plus, Eye, EyeOff, Globe, AlertTriangle } from 'lucide-react';

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Checkbox,
  Chip,
  EmptyState,
  Input,
  Link,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  SelectSection,
  Switch,
  Tabs,
  Tab,
  TextArea,
} from '@enfomentis/ui';
import { useIsDesktop } from '@/hooks';

import { AccountsProvider, useAccounts } from './context';
import { AccountCreateModal, AccountRenameModal } from './components';
import { RiskTabContent } from './Risk';
import { TransactionsTabContent } from './Transactions';
import { MobileAccountList, MobileAccountCreate, MobileAccountDetail } from './pages';
import { CurrencyChooserModal } from '@/components/shared/CurrencyChooser/CurrencyChooserModal';
import { UnsavedChangesDialog } from '@/components/shared/UnsavedChangesDialog';
import { brokerService } from '@/services/BrokerService';
import { ACCOUNT_TYPE_OPTIONS } from '@/constants/accountType';
import { CATEGORY_COLORS } from '@/constants/colors';
import { PROP_FIRM_OPTIONS, PROP_FIRM_ASSET_CLASS_OPTIONS, ACCOUNT_STAGE_OPTIONS } from '@/constants/propFirm';
import { CURRENCY_DATABASE } from '@/constants';
import { getCurrencyInfo } from '@/utils';
import { ASSET_CLASS_TYPE_DISPLAY_MAP } from '@/constants/assetClass';
import { toDisplayValue } from '@/utils/moneyHelpers';
import type { Broker } from '@/types/schema/Broker';
import type { AccountType } from '@/types/shared/AccountType';
import type { AccountFormState } from './context/AccountsContext';

/**
 * Special keys for action items in Select dropdown
 */
const NEW_ACCOUNT_KEY = '__new_account__';
const TOGGLE_ARCHIVED_KEY = '__toggle_archived__';

/**
 * Tab keys for Account Detail section
 */
type AccountDetailTab = 'general' | 'broker-fees' | 'risk' | 'transactions';

/**
 * Inner component that consumes the context.
 * Separated to allow the provider to wrap at the appropriate level.
 */
const AccountsPageContent: React.FC = () => {
  const isDesktop = useIsDesktop();

  const {
    accounts,
    filteredAccounts,
    selectedAccount,
    selectedAccountId,
    loading,
    activeBrokers,
    brokersLoading,
    showArchived,
    isHydrating,
    formState,
    setFormState,
    isDirty,
    isSaving,
    saveSelectedAccount,
    discardSelectedAccountChanges,
    renameSelectedAccount,
    showConflictBanner,
    acceptExternalChanges,
    keepEditing,
    selectAccount,
    setShowArchived,
    archiveAccount,
    restoreAccount,
    checkRestoreBlocked,
    restoreLinkedBrokerAndAccount,
  } = useAccounts();

  const navigate = useNavigate();

  // ===========================================================================
  // UI State
  // ===========================================================================

  // Controlled Select state (prevents close when toggle is clicked)
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const toggleClickedRef = useRef(false);

  // Create modal visibility
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Rename modal visibility
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  // Restore-broker confirmation modal
  const [isRestoreBrokerModalOpen, setIsRestoreBrokerModalOpen] = useState(false);
  const [pendingRestoreBrokerInfo, setPendingRestoreBrokerInfo] = useState<{ brokerId: string; brokerName: string } | null>(null);

  // Active tab for Account Config section
  const [activeTab, setActiveTab] = useState<AccountDetailTab>('general');

  // Currency chooser modal
  const [showCurrencyChooser, setShowCurrencyChooser] = useState(false);

  // Tag input
  const [tagInput, setTagInput] = useState('');

  // Color picker - custom hex state
  const [customHex, setCustomHex] = useState('');
  const [isCustomColor, setIsCustomColor] = useState(false);

  // Unsaved changes dialog — pendingAccountId and pendingAction are mutually exclusive
  const [pendingAccountId, setPendingAccountId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'archive' | null>(null);

  // Archived broker lookup — fetches broker by ID when not in activeBrokers
  const [linkedBroker, setLinkedBroker] = useState<Broker | null>(null);

  useEffect(() => {
    if (!formState.brokerId) {
      setLinkedBroker(null);
      return;
    }
    const inActive = activeBrokers.some((b) => b.id === formState.brokerId);
    if (inActive) {
      setLinkedBroker(null);
      return;
    }
    let cancelled = false;
    brokerService.get(formState.brokerId).then((broker) => {
      if (!cancelled) {
        setLinkedBroker(broker ?? null);
      }
    });
    return () => { cancelled = true; };
  }, [formState.brokerId, activeBrokers]);

  // Reset local UI state when account selection changes
  useEffect(() => {
    setTagInput('');
    setActiveTab('general');
    setIsRenameModalOpen(false);
    setPendingAccountId(null);
    setPendingAction(null);
  }, [selectedAccountId]);

  // Sync color picker UI from authoritative formState.color
  // Single source of truth — covers selection changes, discard, accept, and external sync
  useEffect(() => {
    const color = formState.color;
    const isPreset = CATEGORY_COLORS.some((c) => c.value === color);
    if (color && !isPreset) {
      setCustomHex(color);
      setIsCustomColor(true);
    } else {
      setCustomHex('');
      setIsCustomColor(false);
    }
  }, [formState.color]);

  // ===========================================================================
  // Computed Values
  // ===========================================================================

  const isFormValid = formState.name.trim() !== '' && formState.brokerId !== '';

  // Unsaved changes dialog
  const isUnsavedDialogOpen = pendingAccountId !== null || pendingAction !== null;

  // Filter accounts by archived status
  const activeAccounts = accounts.filter((a) => a.status === 'active');
  const archivedAccounts = accounts.filter((a) => a.status === 'archived');

  // ===========================================================================
  // URL Sync (Desktop Only) - Push selection to URL
  // ===========================================================================

  const [searchParams, setSearchParams] = useSearchParams();

  // Push selection state to URL after hydration completes
  useEffect(() => {
    // Guard: only run after hydration, on desktop
    if (isHydrating || !isDesktop) return;

    // Build new params based on current selection
    const newParams = new URLSearchParams();
    if (selectedAccountId) {
      newParams.set('accountId', selectedAccountId);
    }

    // Compare with current URL params to avoid redundant replaceState
    const currentAccountParam = searchParams.get('accountId');
    const newAccountParam = newParams.get('accountId');

    if (currentAccountParam !== newAccountParam) {
      setSearchParams(newParams, { replace: true });
    }
  }, [isHydrating, isDesktop, selectedAccountId, searchParams, setSearchParams]);

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleNewAccount = useCallback((): void => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCreateModalSave = useCallback((newAccountId: string): void => {
    selectAccount(newAccountId);
  }, [selectAccount]);

  const handleAccountSelect = useCallback(
    (keys: 'all' | Set<React.Key>): void => {
      if (keys === 'all') return;
      const selected = Array.from(keys)[0] as string | undefined;
      if (!selected) return;

      // Handle "New Account" action
      if (selected === NEW_ACCOUNT_KEY) {
        handleNewAccount();
        return;
      }

      // Handle "Show Archived" toggle action
      if (selected === TOGGLE_ARCHIVED_KEY) {
        toggleClickedRef.current = true;
        setShowArchived(!showArchived);
        return;
      }

      // Normal account selection — dirty guard
      if (selected !== selectedAccountId) {
        if (isDirty) {
          setPendingAccountId(selected);
          setPendingAction(null); // mutual exclusion
          return;
        }
        selectAccount(selected);
      }
    },
    [selectedAccountId, showArchived, setShowArchived, handleNewAccount, selectAccount, isDirty]
  );

  const handleSelectOpenChange = useCallback((open: boolean): void => {
    if (!open && toggleClickedRef.current) {
      // Toggle was clicked - reset ref and keep Select open
      toggleClickedRef.current = false;
      return;
    }
    setIsSelectOpen(open);
  }, []);

  const handleTabChange = useCallback((key: React.Key): void => {
    setActiveTab(key as AccountDetailTab);
  }, []);

  // ===========================================================================
  // CRUD & Dirty State Handlers
  // ===========================================================================

  const handleDiscard = useCallback(async (): Promise<void> => {
    await discardSelectedAccountChanges();
    setTagInput('');
  }, [discardSelectedAccountChanges]);

  const handleArchive = useCallback((): void => {
    if (isSaving) return;
    if (isDirty) {
      setPendingAction('archive');
      setPendingAccountId(null); // mutual exclusion
      return;
    }
    archiveAccount();
  }, [isSaving, isDirty, archiveAccount]);

  const handleRestore = useCallback(async (): Promise<void> => {
    if (isSaving) return;
    const result = await checkRestoreBlocked();
    if (result.blocked) {
      setPendingRestoreBrokerInfo({ brokerId: result.brokerId, brokerName: result.brokerName });
      setIsRestoreBrokerModalOpen(true);
      return;
    }
    restoreAccount();
  }, [isSaving, checkRestoreBlocked, restoreAccount]);

  const handleRestoreBrokerAndAccount = useCallback(async (): Promise<void> => {
    setIsRestoreBrokerModalOpen(false);
    setPendingRestoreBrokerInfo(null);
    await restoreLinkedBrokerAndAccount();
  }, [restoreLinkedBrokerAndAccount]);

  const handleRenameSubmit = useCallback(async (newName: string): Promise<boolean> => {
    return renameSelectedAccount(newName);
  }, [renameSelectedAccount]);

  // ===========================================================================
  // Unsaved Changes Dialog Handlers
  // ===========================================================================

  const handleUnsavedDialogCancel = useCallback((): void => {
    setPendingAccountId(null);
    setPendingAction(null);
  }, []);

  const handleUnsavedDialogDiscard = useCallback(async (): Promise<void> => {
    // Reset form to baseline (await queue-apply if needed)
    await handleDiscard();

    // Execute deferred action
    if (pendingAccountId) {
      selectAccount(pendingAccountId);
      setPendingAccountId(null);
    }
    if (pendingAction === 'archive') {
      archiveAccount();
      setPendingAction(null);
    }
  }, [pendingAccountId, pendingAction, handleDiscard, selectAccount, archiveAccount]);

  // ===========================================================================
  // Form Handlers
  // ===========================================================================

  const handleFormFieldChange = useCallback(<K extends keyof AccountFormState>(
    field: K,
    value: AccountFormState[K],
  ): void => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, [setFormState]);

  const handlePropFirmChange = useCallback(<K extends keyof AccountFormState['propFirmDetails']>(
    field: K,
    value: AccountFormState['propFirmDetails'][K],
  ): void => {
    setFormState((prev) => ({
      ...prev,
      propFirmDetails: { ...prev.propFirmDetails, [field]: value },
    }));
  }, [setFormState]);

  const handleFeePreferenceChange = useCallback((
    field: keyof AccountFormState['feePreferences'],
    value: boolean,
  ): void => {
    setFormState((prev) => ({
      ...prev,
      feePreferences: { ...prev.feePreferences, [field]: value },
    }));
  }, [setFormState]);

  const handleAccountTypeChange = useCallback((value: AccountType): void => {
    setFormState((prev) => ({
      ...prev,
      accountType: value,
      // Clear conditional fields when switching away
      ...(value !== 'other' ? { customAccountTypeName: '' } : {}),
      ...(value !== 'prop' ? {
        propFirmDetails: {
          firmName: 'apex' as const,
          customFirmName: '',
          assetClass: 'futures' as const,
          customAssetClass: '',
          accountStage: 'evaluation' as const,
        },
      } : {}),
    }));
  }, [setFormState]);

  const handleColorChange = useCallback((value: string): void => {
    if (value === '__custom__') {
      setIsCustomColor(true);
      // Don't update formState.color until valid hex is entered
    } else {
      setIsCustomColor(false);
      setCustomHex('');
      handleFormFieldChange('color', value);
    }
  }, [handleFormFieldChange]);

  const handleCustomHexChange = useCallback((value: string): void => {
    setCustomHex(value);
    const trimmed = value.trim();
    if (/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) {
      const normalized = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
      handleFormFieldChange('color', normalized);
    }
  }, [handleFormFieldChange]);

  const handleCurrencySelect = useCallback((code: string): void => {
    handleFormFieldChange('baseCurrency', code);
    setShowCurrencyChooser(false);
  }, [handleFormFieldChange]);

  const addTag = useCallback((value: string): void => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const normalized = trimmed.toLowerCase();
    setFormState((prev) => {
      if (prev.tags.some((t) => t.toLowerCase() === normalized)) return prev;
      return { ...prev, tags: [...prev.tags, trimmed] };
    });
    setTagInput('');
  }, [setFormState]);

  const removeTag = useCallback((tag: string): void => {
    setFormState((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  }, [setFormState]);

  const handleTagKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
  }, [addTag, tagInput]);

  // ===========================================================================
  // Computed Values (Form)
  // ===========================================================================

  const activeBrokerMatch = activeBrokers.find((b) => b.id === formState.brokerId);
  const selectedBrokerForForm = activeBrokerMatch ?? linkedBroker;
  const brokerCurrency = selectedBrokerForForm?.feeMetadata?.defaultAccountCurrency ?? 'USD';
  const currencyCode = formState.baseCurrency || brokerCurrency;
  const currencyInfo = getCurrencyInfo(currencyCode, CURRENCY_DATABASE);
  const isValidCustomHex = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(customHex.trim());

  // Fee schedule derived values (Broker & Fees tab — read-only display)
  const feeScheduleConfigs = selectedBrokerForForm?.feeConfigurations.filter(c => c.status === 'active') ?? [];
  const feeScheduleAssetGroups = feeScheduleConfigs.reduce<Record<string, number>>((acc, cfg) => {
    const key = cfg.category?.assetClass ?? 'other';
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
  const feeSchedulePlatform = selectedBrokerForForm?.feeMetadata?.platformFees;
  const feeScheduleCurrCode = selectedBrokerForForm?.feeMetadata?.defaultAccountCurrency ?? 'USD';
  const feeScheduleCurrInfo = getCurrencyInfo(feeScheduleCurrCode, CURRENCY_DATABASE);
  const feeSchedulePrecision = feeScheduleCurrInfo?.minorUnit ?? selectedBrokerForForm?.feeMetadata?.minimumBalance?.precision ?? 2;
  const feeScheduleSymbol = feeScheduleCurrInfo?.symbol ?? feeScheduleCurrCode;
  const hasNonZeroPlatformFees = !!(feeSchedulePlatform && (
    (feeSchedulePlatform.monthlyFee !== undefined && feeSchedulePlatform.monthlyFee !== 0) ||
    (feeSchedulePlatform.dataFees !== undefined && feeSchedulePlatform.dataFees !== 0) ||
    (feeSchedulePlatform.inactivityFee !== undefined && feeSchedulePlatform.inactivityFee !== 0)
  ));
  const hasOvernightDefault = !!selectedBrokerForForm?.brokerOvernightFinancingDefault;
  const hasFxDefault = !!selectedBrokerForForm?.brokerCurrencyConversionDefault;
  const isFeeScheduleEmpty = !!selectedBrokerForForm && feeScheduleConfigs.length === 0
    && !hasNonZeroPlatformFees && !hasOvernightDefault && !hasFxDefault;

  // ===========================================================================
  // Render
  // ===========================================================================

  // Desktop layout - stacked cards
  if (isDesktop) {
    const hasBrokers = activeBrokers.length > 0;
    const hasAccounts = accounts.length > 0;
    const hasFilteredAccounts = filteredAccounts.length > 0;

    // Dependency gate: loading state
    if (brokersLoading || loading) {
      return (
        <div className="flex flex-col gap-6 w-full h-full">
          <Card shadow="sm" radius="sm">
            <CardBody>
              <div className="text-center py-4">
                <p className="text-text-secondary">Loading...</p>
              </div>
            </CardBody>
          </Card>
        </div>
      );
    }

    // Dependency gate: no active brokers AND no accounts
    if (!hasBrokers && !hasAccounts) {
      return (
        <div className="flex flex-col gap-6 w-full h-full">
          <Card shadow="sm" radius="sm">
            <CardBody>
              <EmptyState
                title="No brokers configured"
                description="Create or import a broker before setting up an account."
                action={
                  <Button
                    color="primary"
                    onPress={() => navigate('/settings/portfolio/reference-data?tab=brokers')}
                  >
                    Go to Brokers
                  </Button>
                }
              />
            </CardBody>
          </Card>
        </div>
      );
    }

    return (
      <div className="flex flex-col w-full h-full">
        {/* Account Section */}
        <Card shadow="sm" radius="sm" className="flex-1 min-h-0 flex flex-col">
          <CardHeader className="flex flex-row items-center gap-2">
            {/* Account Select with label (replaces heading) */}
            {hasAccounts && (
              <Select
                label="Account:"
                aria-label="Select account"
                placeholder="Select account"
                selectedKeys={selectedAccountId ? [selectedAccountId] : []}
                onSelectionChange={handleAccountSelect}
                isOpen={isSelectOpen}
                onOpenChange={handleSelectOpenChange}
                className="flex w-full"
                isDisabled={loading}
                {...(!hasBrokers ? { disabledKeys: [NEW_ACCOUNT_KEY] } : {})}
                labelPlacement="outside-left"
                classNames={{
                  label: 'text-lg font-semibold text-text-primary',
                  trigger: 'bg-transparent shadow-none data-[hover=true]:bg-default-50',
                  value: '!text-text-primary text-lg',
                }}
                listboxProps={{
                  itemClasses: {
                    base: 'data-[hover=true]:!text-primary data-[hover=true]:!bg-transparent',
                  },
                }}
              >
                {[
                  // Active accounts section
                  ...(activeAccounts.length > 0
                    ? [
                        <SelectSection key="active" title="Active" showDivider>
                          {activeAccounts.map((account) => (
                            <SelectItem key={account.id}>{account.name}</SelectItem>
                          ))}
                        </SelectSection>,
                      ]
                    : []),
                  // Actions section (New + Show Archived toggle)
                  <SelectSection
                    key="__actions_section__"
                    showDivider={showArchived && archivedAccounts.length > 0}
                  >
                    <SelectItem
                      key={NEW_ACCOUNT_KEY}
                      startContent={<Plus size={16} />}
                      className="text-secondary"
                    >
                      New Account
                    </SelectItem>
                    <SelectItem
                      key={TOGGLE_ARCHIVED_KEY}
                      startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                    >
                      Archived
                    </SelectItem>
                  </SelectSection>,
                  // Archived accounts section (only when showArchived is on)
                  ...(showArchived && archivedAccounts.length > 0
                    ? [
                        <SelectSection key="archived" title="Archived">
                          {archivedAccounts.map((account) => (
                            <SelectItem key={account.id}>{account.name}</SelectItem>
                          ))}
                        </SelectSection>,
                      ]
                    : []),
                ]}
              </Select>
            )}

            {/* When no accounts, show heading instead */}
            {!hasAccounts && (
              <span className="text-lg font-semibold text-text-primary">Accounts</span>
            )}

            <div className="flex items-center gap-2 ml-auto">
              <Button
                size="sm"
                variant="light"
                isDisabled={!selectedAccount || loading || isSaving || isDirty}
                onPress={() => setIsRenameModalOpen(true)}
              >
                Rename
              </Button>
            </div>
          </CardHeader>

          {/* Broker dependency warning */}
          {!hasBrokers && hasAccounts && (
            <div className="flex items-center gap-2 px-3 py-2 border-t border-default-200">
              <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
              <p className="text-xs text-text-tertiary">
                No active brokers — <Link size="sm" onPress={() => navigate('/settings/portfolio/reference-data?tab=brokers')} className="cursor-pointer">create a broker</Link> to add new accounts
              </p>
            </div>
          )}

          {/* CardBody - when account selected OR empty states need to display */}
          {(selectedAccount || !hasAccounts || !hasFilteredAccounts) && (
            <CardBody className="flex-1 min-h-0 flex flex-col space-y-4 border-t border-default-200">
              {/* Empty State - No accounts */}
              {!hasAccounts && (
                <EmptyState
                  title="No accounts yet"
                  description="Create your first account to get started."
                  action={
                    <Button
                      startContent={<Plus size={16} />}
                      onPress={handleNewAccount}
                    >
                      New Account
                    </Button>
                  }
                />
              )}

              {/* Empty State - All archived */}
              {hasAccounts && !hasFilteredAccounts && (
                <EmptyState
                  title="No active accounts"
                  description="All accounts are archived"
                  action={
                    <Button
                      variant="flat"
                      startContent={<Eye size={16} />}
                      onPress={() => setShowArchived(true)}
                    >
                      Show Archived
                    </Button>
                  }
                />
              )}

              {/* Account Detail — inner Card pins tabs, body scrolls */}
              {selectedAccount && (
                <Card shadow="none" radius="none" className="flex-1 min-h-0 flex flex-col">
                  <CardHeader className="flex flex-col items-stretch gap-4 px-0 py-0">
                    {/* Conflict Banner */}
                    {showConflictBanner && (
                      <div className="rounded-lg bg-warning-50 border border-warning-200 p-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-warning-600 shrink-0" />
                            <span className="text-sm text-warning-700">
                              Account updated in another tab
                            </span>
                          </div>
                          <div className="flex flex-col gap-2 md:flex-row">
                            <Button size="sm" variant="flat" onPress={keepEditing}>
                              Keep editing
                            </Button>
                            <Button size="sm" color="warning" onPress={acceptExternalChanges}>
                              Accept changes
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    <Tabs
                      selectedKey={activeTab}
                      onSelectionChange={handleTabChange}
                      variant="underlined"
                      color="primary"
                      classNames={{
                        tabList: 'px-0 justify-start',
                        cursor: 'bg-primary',
                      }}
                      aria-label="Account detail tabs"
                    >
                      <Tab key="general" title="General" />
                      <Tab key="broker-fees" title="Broker & Fees" />
                      <Tab key="risk" title="Risk" />
                      <Tab key="transactions" title="Transactions" />
                    </Tabs>
                  </CardHeader>

                  <CardBody className="flex-1 min-h-0 flex flex-col px-0">
                  {/* General Tab */}
                  {activeTab === 'general' && (
                    <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
                      {/* Identity + Organization */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {/* Core Details */}
                        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
                          <CardHeader className="px-1 pt-0 pb-2 min-h-10 text-sm font-semibold text-text-primary border-b-1 border-default-200">
                            Core Details
                          </CardHeader>
                          <CardBody className="px-1 pb-0 space-y-3">
                            <Select
                              label="Account Type"
                              labelPlacement="outside"
                              selectedKeys={[formState.accountType]}
                              onSelectionChange={(keys) => {
                                if (keys === 'all') return;
                                const val = Array.from(keys)[0] as AccountType | undefined;
                                if (val) handleAccountTypeChange(val);
                              }}
                              isRequired
                            >
                              {ACCOUNT_TYPE_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} textValue={opt.label}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </Select>
                            {formState.accountType === 'other' && (
                              <Input
                                label="Custom Type Name"
                                labelPlacement="outside"
                                placeholder="Enter custom account type..."
                                value={formState.customAccountTypeName}
                                onValueChange={(val) => handleFormFieldChange('customAccountTypeName', val)}
                              />
                            )}
                            <Input
                              label="Account Number"
                              labelPlacement="outside"
                              placeholder="Optional account number"
                              value={formState.accountNumber}
                              onValueChange={(val) => handleFormFieldChange('accountNumber', val)}
                            />
                            <TextArea
                              label="Description"
                              labelPlacement="outside-top"
                              placeholder="Optional description or notes..."
                              value={formState.description}
                              onValueChange={(val) => handleFormFieldChange('description', val)}
                              minRows={3}
                              maxRows={3}
                              className="[&_textarea]:text-base md:[&_textarea]:text-sm [&_textarea]:pt-2 p-1"
                            />
                          </CardBody>
                        </Card>

                        {/* Organization */}
                        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
                          <CardHeader className="px-1 pt-0 pb-2 min-h-10 text-sm font-semibold text-text-primary border-b-1 border-default-200">
                            Organization
                          </CardHeader>
                          <CardBody className="px-1 pb-0 space-y-3">
                            {/* Color Picker */}
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-text-primary">Color</label>
                              <RadioGroup
                                value={isCustomColor ? '__custom__' : formState.color}
                                onValueChange={handleColorChange}
                                orientation="horizontal"
                                classNames={{ wrapper: 'gap-3 flex-wrap' }}
                              >
                                {CATEGORY_COLORS.map((color) => (
                                  <Radio
                                    key={color.value}
                                    value={color.value}
                                    classNames={{ base: 'm-0' }}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-6 h-6 rounded-full border-2 border-default-300"
                                        style={{ backgroundColor: color.value }}
                                      />
                                      <span className="text-sm">{color.name}</span>
                                    </div>
                                  </Radio>
                                ))}
                                <Radio
                                  value="__custom__"
                                  classNames={{ base: 'm-0' }}
                                >
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-6 h-6 rounded-full border-2 border-default-300"
                                      style={{
                                        backgroundColor: isCustomColor && isValidCustomHex
                                          ? (customHex.trim().startsWith('#') ? customHex.trim() : `#${customHex.trim()}`)
                                          : '#cccccc',
                                      }}
                                    />
                                    <span className="text-sm">Custom</span>
                                  </div>
                                </Radio>
                              </RadioGroup>
                              {isCustomColor && (
                                <div className="flex items-center gap-3 mt-2">
                                  <Input
                                    label="Hex Color"
                                    placeholder="#ff5500"
                                    value={customHex}
                                    onValueChange={handleCustomHexChange}
                                    variant="bordered"
                                    size="sm"
                                    className="max-w-40"
                                    isInvalid={customHex.length > 0 && !isValidCustomHex}
                                    errorMessage={
                                      customHex.length > 0 && !isValidCustomHex
                                        ? 'Invalid hex color'
                                        : undefined
                                    }
                                  />
                                  {isValidCustomHex && (
                                    <div
                                      className="w-8 h-8 rounded-full border-2 border-default-300 shrink-0"
                                      style={{ backgroundColor: customHex.trim().startsWith('#') ? customHex.trim() : `#${customHex.trim()}` }}
                                    />
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Tags */}
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-text-primary">Tags</label>
                              <Input
                                placeholder="Add tag and press Enter..."
                                value={tagInput}
                                onValueChange={setTagInput}
                                onKeyDown={handleTagKeyDown}
                                size="sm"
                              />
                              {formState.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {formState.tags.map((tag) => (
                                    <Chip
                                      key={tag}
                                      size="sm"
                                      variant="flat"
                                      onClose={() => removeTag(tag)}
                                    >
                                      {tag}
                                    </Chip>
                                  ))}
                                </div>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </div>

                      {/* Prop Firm Details (conditional) */}
                      {formState.accountType === 'prop' && (
                        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
                          <CardHeader className="px-1 pt-0 pb-2 min-h-10 text-sm font-semibold text-text-primary border-b-1 border-default-200">
                            Prop Firm Details
                          </CardHeader>
                          <CardBody className="px-1 pb-0 space-y-3">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                              <Select
                                label="Firm Name"
                                labelPlacement="outside"
                                selectedKeys={[formState.propFirmDetails.firmName]}
                                onSelectionChange={(keys) => {
                                  if (keys === 'all') return;
                                  const val = Array.from(keys)[0] as typeof formState.propFirmDetails.firmName | undefined;
                                  if (val) {
                                    handlePropFirmChange('firmName', val);
                                    if (val !== 'other') handlePropFirmChange('customFirmName', '');
                                  }
                                }}
                              >
                                {PROP_FIRM_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} textValue={opt.label}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </Select>
                              <Select
                                label="Asset Class"
                                labelPlacement="outside"
                                selectedKeys={[formState.propFirmDetails.assetClass]}
                                onSelectionChange={(keys) => {
                                  if (keys === 'all') return;
                                  const val = Array.from(keys)[0] as typeof formState.propFirmDetails.assetClass | undefined;
                                  if (val) {
                                    handlePropFirmChange('assetClass', val);
                                    if (val !== 'other') handlePropFirmChange('customAssetClass', '');
                                  }
                                }}
                              >
                                {PROP_FIRM_ASSET_CLASS_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} textValue={opt.label}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </Select>
                              <Select
                                label="Account Stage"
                                labelPlacement="outside"
                                selectedKeys={[formState.propFirmDetails.accountStage]}
                                onSelectionChange={(keys) => {
                                  if (keys === 'all') return;
                                  const val = Array.from(keys)[0] as typeof formState.propFirmDetails.accountStage | undefined;
                                  if (val) handlePropFirmChange('accountStage', val);
                                }}
                              >
                                {ACCOUNT_STAGE_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} textValue={opt.label}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </Select>
                            </div>
                            {formState.propFirmDetails.firmName === 'other' && (
                              <Input
                                label="Custom Firm Name"
                                labelPlacement="outside"
                                placeholder="Enter prop firm name..."
                                value={formState.propFirmDetails.customFirmName}
                                onValueChange={(val) => handlePropFirmChange('customFirmName', val)}
                              />
                            )}
                            {formState.propFirmDetails.assetClass === 'other' && (
                              <Input
                                label="Custom Asset Class"
                                labelPlacement="outside"
                                placeholder="Enter asset class..."
                                value={formState.propFirmDetails.customAssetClass}
                                onValueChange={(val) => handlePropFirmChange('customAssetClass', val)}
                              />
                            )}
                          </CardBody>
                        </Card>
                      )}
                    </div>
                  )}

                  {/* Broker & Fees Tab */}
                  {activeTab === 'broker-fees' && (
                    <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {/* Left column: Linked Broker + Fee Tracking */}
                        <div className="space-y-3">
                          {/* Linked Broker */}
                          <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
                            <CardHeader className="px-1 pt-0 pb-2 min-h-10 text-sm font-semibold text-text-primary border-b-1 border-default-200">
                              Linked Broker
                            </CardHeader>
                            <CardBody className="px-1 pb-0 space-y-3">
                              <Select
                                // label="Broker"
                                // labelPlacement="outside"
                                aria-label='Broker Selection'
                                selectedKeys={formState.brokerId && (activeBrokerMatch || linkedBroker) ? [formState.brokerId] : []}
                                onSelectionChange={(keys) => {
                                  if (keys === 'all') return;
                                  const val = Array.from(keys)[0] as string | undefined;
                                  if (val) handleFormFieldChange('brokerId', val);
                                }}
                                isRequired
                                {...(linkedBroker ? { disabledKeys: [linkedBroker.id] } : {})}
                              >
                                {[
                                  // Archived linked broker (disabled, shown for context)
                                  ...(linkedBroker
                                    ? [
                                        <SelectItem key={linkedBroker.id} textValue={`${linkedBroker.name} (Archived)`}>
                                          {linkedBroker.name} (Archived)
                                        </SelectItem>,
                                      ]
                                    : []),
                                  // Active brokers (selectable)
                                  ...activeBrokers.map((broker) => (
                                    <SelectItem key={broker.id} textValue={broker.name}>
                                      {broker.name}
                                    </SelectItem>
                                  )),
                                ]}
                              </Select>
                              {selectedBrokerForForm ? (
                                <p className="text-xs text-text-tertiary">
                                  {selectedBrokerForForm.name} &middot; Default: {brokerCurrency}
                                </p>
                              ) : formState.brokerId ? (
                                <p className="text-xs text-text-tertiary">
                                  Linked broker unavailable
                                </p>
                              ) : null}
                              <div className="min-w-0 space-y-2">
                                <Switch
                                  size="sm"
                                  isSelected={formState.baseCurrency !== ''}
                                  onValueChange={(on) => {
                                    if (on) {
                                      handleFormFieldChange('baseCurrency', brokerCurrency);
                                    } else {
                                      handleFormFieldChange('baseCurrency', '');
                                    }
                                  }}
                                >
                                  Override broker default currency
                                </Switch>
                                {formState.baseCurrency ? (
                                  <Button
                                    variant="bordered"
                                    className="w-full justify-between font-normal border-default-200"
                                    endContent={<Globe className="w-4 h-4 text-text-tertiary shrink-0" />}
                                    onPress={() => setShowCurrencyChooser(true)}
                                  >
                                    {currencyInfo ? (
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{currencyInfo.symbol}</span>
                                        <span>{currencyInfo.name}</span>
                                        <span className="text-text-secondary">({currencyCode})</span>
                                      </div>
                                    ) : (
                                      <span>{currencyCode}</span>
                                    )}
                                  </Button>
                                ) : (
                                  <p className="text-xs text-text-tertiary">
                                    Using broker default: {brokerCurrency}{currencyInfo ? ` — ${currencyInfo.name}` : ''}
                                  </p>
                                )}
                              </div>
                            </CardBody>
                          </Card>

                          {/* Fee Tracking */}
                          <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
                            <CardHeader className="px-1 pt-0 pb-2 min-h-10 text-sm font-semibold text-text-primary border-b-1 border-default-200">
                              Fee Tracking
                            </CardHeader>
                            <CardBody className="px-1 pb-1 space-y-3 overflow-visible">
                              <Checkbox
                                isSelected={formState.feePreferences.includeFeeInPnL}
                                onValueChange={(val) => handleFeePreferenceChange('includeFeeInPnL', val)}
                              >
                                Include fees in P&amp;L calculations
                              </Checkbox>
                              <Checkbox
                                isSelected={formState.feePreferences.promptFeeVerification}
                                onValueChange={(val) => handleFeePreferenceChange('promptFeeVerification', val)}
                              >
                                Prompt fee verification on new instruments
                              </Checkbox>
                              <Checkbox
                                isSelected={formState.feePreferences.trackOvernightFinancing}
                                onValueChange={(val) => handleFeePreferenceChange('trackOvernightFinancing', val)}
                              >
                                Track overnight financing costs
                              </Checkbox>
                              <Checkbox
                                isSelected={formState.feePreferences.trackCurrencyConversion}
                                onValueChange={(val) => handleFeePreferenceChange('trackCurrencyConversion', val)}
                              >
                                Track currency conversion costs
                              </Checkbox>
                            </CardBody>
                          </Card>
                        </div>

                        {/* Right column: Fee Schedule */}
                        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
                          <CardHeader className="px-1 pt-0 pb-2 min-h-10 text-sm font-semibold text-text-primary border-b-1 border-default-200">
                            Fee Schedule
                          </CardHeader>
                          <CardBody className="px-1 pb-1 space-y-3">
                            {selectedBrokerForForm ? (
                              isFeeScheduleEmpty ? (
                                <p className="text-sm text-text-tertiary py-4 text-center">
                                  No fees configured for this broker
                                </p>
                              ) : (
                                <>
                                  <p className="text-sm text-text-secondary">
                                    {feeScheduleConfigs.length} {feeScheduleConfigs.length === 1 ? 'configuration' : 'configurations'} across{' '}
                                    {Object.keys(feeScheduleAssetGroups).length} {Object.keys(feeScheduleAssetGroups).length === 1 ? 'asset class' : 'asset classes'}
                                  </p>
                                  {Object.keys(feeScheduleAssetGroups).length > 0 && (
                                    <div className="space-y-1">
                                      {Object.entries(feeScheduleAssetGroups)
                                        .sort(([a], [b]) => a.localeCompare(b))
                                        .map(([key, count]) => (
                                          <p key={key} className="text-xs text-text-tertiary">
                                            {key === 'other' ? 'Other' : ASSET_CLASS_TYPE_DISPLAY_MAP[key as keyof typeof ASSET_CLASS_TYPE_DISPLAY_MAP]}:{' '}
                                            {count} {count === 1 ? 'config' : 'configs'}
                                          </p>
                                        ))}
                                    </div>
                                  )}
                                  <div className="text-xs text-text-tertiary">
                                    <span className="font-medium text-text-secondary">Platform Fees: </span>
                                    {hasNonZeroPlatformFees ? (
                                      [
                                        feeSchedulePlatform?.monthlyFee ? `Monthly: ${feeScheduleSymbol}${(toDisplayValue(feeSchedulePlatform.monthlyFee, feeSchedulePrecision) ?? 0).toFixed(feeSchedulePrecision)}` : null,
                                        feeSchedulePlatform?.dataFees ? `Data: ${feeScheduleSymbol}${(toDisplayValue(feeSchedulePlatform.dataFees, feeSchedulePrecision) ?? 0).toFixed(feeSchedulePrecision)}` : null,
                                        feeSchedulePlatform?.inactivityFee ? `Inactivity: ${feeScheduleSymbol}${(toDisplayValue(feeSchedulePlatform.inactivityFee, feeSchedulePrecision) ?? 0).toFixed(feeSchedulePrecision)}` : null,
                                      ].filter(Boolean).join(' \u00b7 ')
                                    ) : (
                                      'None configured'
                                    )}
                                  </div>
                                  <div className="text-xs text-text-tertiary">
                                    <span className="font-medium text-text-secondary">Overnight Financing: </span>
                                    {hasOvernightDefault ? 'Configured' : 'Not configured'}
                                  </div>
                                  <div className="text-xs text-text-tertiary">
                                    <span className="font-medium text-text-secondary">Currency Conversion: </span>
                                    {hasFxDefault ? 'Configured' : 'Not configured'}
                                  </div>
                                </>
                              )
                            ) : (
                              <p className="text-sm text-text-tertiary py-4 text-center">
                                Select a broker to view fee schedule
                              </p>
                            )}
                            
                          </CardBody>
                          {selectedBrokerForForm && (
                            <CardFooter className="pt-3 pb-2 px-2 border-t border-default-200 items-center justify-between gap-4">
                              <p className="text-xs text-text-tertiary">
                                View and manage fee configurations
                              </p>
                              <Link
                                onPress={() => navigate(`/settings/portfolio/reference-data?tab=brokers&brokerId=${formState.brokerId}`)}
                                showAnchorIcon
                                size="sm"
                                className="cursor-pointer shrink-0"
                              >
                                View Broker Settings
                              </Link>
                            </CardFooter>
                          )}
                        </Card>
                      </div>
                    </div>
                  )}

                  {/* Risk Tab */}
                  {activeTab === 'risk' && (
                    <div className="flex-1 min-h-0 flex flex-col">
                      <RiskTabContent />
                    </div>
                  )}

                  {/* Transactions Tab */}
                  {activeTab === 'transactions' && (
                    <div className="flex-1 min-h-0 flex flex-col">
                      <TransactionsTabContent />
                    </div>
                  )}

                  </CardBody>
                </Card>
              )}
            </CardBody>
          )}

          {/* CardFooter - when account selected */}
          {selectedAccount && (
            <CardFooter className="flex items-center justify-between gap-4 border-t border-default-200">
              <div className="flex items-center gap-3">
                {/* Archive/Restore Button */}
                {selectedAccount.status === 'archived' ? (
                  <Button
                    variant="flat"
                    size="sm"
                    isDisabled={isSaving}
                    onPress={handleRestore}
                  >
                    Restore
                  </Button>
                ) : (
                  <Button
                    variant="flat"
                    size="sm"
                    isDisabled={isSaving}
                    onPress={handleArchive}
                  >
                    Archive
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isDirty && (
                  <span className="text-xs text-warning-600 shrink-0 whitespace-nowrap">
                    Unsaved changes
                  </span>
                )}
                <Button
                  variant="flat"
                  size="sm"
                  isDisabled={!isDirty || isSaving}
                  onPress={handleDiscard}
                >
                  Discard
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  isDisabled={!isDirty || !isFormValid || isSaving}
                  isLoading={isSaving}
                  onPress={saveSelectedAccount}
                >
                  Save
                </Button>
              </div>
            </CardFooter>
          )}
        </Card>

        {/* Account Create Modal */}
        <AccountCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSave={handleCreateModalSave}
        />

        {/* Account Rename Modal */}
        <AccountRenameModal
          isOpen={isRenameModalOpen}
          onClose={() => setIsRenameModalOpen(false)}
          currentName={selectedAccount?.name ?? ''}
          onSubmit={handleRenameSubmit}
        />

        {/* Currency Chooser Modal */}
        <CurrencyChooserModal
          isOpen={showCurrencyChooser}
          onClose={() => setShowCurrencyChooser(false)}
          onCurrencySelect={handleCurrencySelect}
          {...(currencyCode ? { selectedCurrency: currencyCode } : {})}
          title="Account Base Currency"
        />

        {/* Unsaved Changes Dialog */}
        <UnsavedChangesDialog
          isOpen={isUnsavedDialogOpen}
          onClose={handleUnsavedDialogCancel}
          onDiscard={handleUnsavedDialogDiscard}
        />

        {/* Restore Broker Confirmation Modal */}
        <Modal
          isOpen={isRestoreBrokerModalOpen}
          onClose={() => {
            setIsRestoreBrokerModalOpen(false);
            setPendingRestoreBrokerInfo(null);
          }}
          size="sm"
        >
          <ModalContent>
            <ModalHeader>Restore Linked Broker?</ModalHeader>
            <ModalBody>
              <p className="text-sm text-text-secondary">
                This account is linked to an archived broker
                {pendingRestoreBrokerInfo ? ` "${pendingRestoreBrokerInfo.brokerName}"` : ''}.
                Restore the broker as well?
              </p>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="flat"
                size="sm"
                onPress={() => {
                  setIsRestoreBrokerModalOpen(false);
                  setPendingRestoreBrokerInfo(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                size="sm"
                onPress={handleRestoreBrokerAndAccount}
              >
                Restore Both
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    );
  }

  // Mobile layout - nested page navigation
  return (
    <Routes>
      <Route index element={<MobileAccountList />} />
      <Route path="new" element={<MobileAccountCreate />} />
      <Route path=":accountId" element={<MobileAccountDetail />} />
    </Routes>
  );
};

/**
 * Accounts Page
 *
 * Wraps the page content with the AccountsProvider.
 */
export const AccountsPage: React.FC = () => {
  return (
    <AccountsProvider>
      <AccountsPageContent />
    </AccountsProvider>
  );
};
